import { scorePrediction } from '../../scoring'
import { tierForPoints, type GameTier } from '../../gameTier'
import { CHIP_SEASON_CAPS } from '../../chipRules'
import type {
  ChipId,
  ChipLedger,
  GameweekDetail,
  GameweekResult,
  SeasonStats,
} from '../../types'

/** Order the three tiles are always rendered in — fixed, so the row never reshuffles itself.
 *  Matches the predict page's chip row (`mockChips.ts`'s `chipDefinitions`), so a chip sits in the
 *  same slot everywhere in the app. */
export const CHIP_TILE_ORDER: ChipId[] = ['doubleUp', 'goalfest', 'cleanSweep']

export const CHIP_TILE_NAMES: Record<ChipId, string> = {
  doubleUp: 'Double Up',
  cleanSweep: 'Clean Sweep',
  goalfest: 'Goalfest',
}

/** The tile face at the narrowest phone widths, where the full name would not fit on one line.
 *  Only Clean Sweep needs one; the other two already fit. */
export const CHIP_TILE_SHORT_NAMES: Record<ChipId, string> = {
  doubleUp: 'Double Up',
  cleanSweep: 'Sweep',
  goalfest: 'Goalfest',
}

export interface BestGameweekRecord extends NonNullable<SeasonStats['bestGameweek']> {
  /** One per fixture, in fixture order. Empty when that gameweek's detail isn't to hand. */
  marks: GameTier[]
  /** The chips `points` was scored with. Empty when the detail isn't to hand — on someone else's
   *  profile it is a lazy fetch, so the chip name can arrive a beat after the number. The number
   *  itself never waits on this, since it comes off the season stats. */
  activeChips: ChipId[]
}

export interface ChipTile {
  id: ChipId
  name: string
  state: 'played' | 'live' | 'unplayed'
  /**
   * The gameweek the tile face shows: for a multi-charge chip, the highest-scoring use, since a
   * record block should lead with the best one rather than a list. Null when unplayed.
   */
  gameweek: number | null
  /** Every gameweek it was played in, ascending — what the modal lists. Empty when unplayed. */
  gameweeks: number[]
  /**
   * `gameweek`'s **full** total, chip effect included — not the chip's isolated delta. Null until
   * that gameweek has settled: the server writes scored rows from the first full-time whistle
   * onwards, so a number read before settlement is a half-played week presented as a record.
   *
   */
  points: number | null
  /** How many of the season's charges are spent, and how many there are — drawn as pips. */
  used: number
  seasonCap: number
}

/**
 * The result mark for each fixture in a gameweek, in fixture order. Exact scores
 * are a subset of correct outcomes, and the marks are what makes that nesting legible — a violet
 * mark is also one of the `N correct` above it.
 */
export function marksForGameweek(detail: GameweekDetail | null | undefined): GameTier[] {
  if (!detail) return []
  return detail.games.map((game) => {
    const actual = detail.results[game.id]
    const prediction = detail.predictions[game.id]
    if (!actual || !prediction) return 'zero'
    return tierForPoints(scorePrediction(prediction, actual).points)
  })
}

/** The line under the sharpest week's total: the chip that was on the week, or null when there
 *  wasn't one. Names the chip and nothing else — the base figure the week was *ranked* on is not
 *  a number the row shows, it's what the ⓘ explains.
 *
 *  Reads off `activeChips` rather than off a gap between the two totals, so a broken Clean Sweep —
 *  which scores 0 — still says which chip did that. */
export function sharpestWeekChipLine(record: BestGameweekRecord): string | null {
  if (record.activeChips.length === 0) return null
  return record.activeChips.map((id) => CHIP_TILE_NAMES[id]).join(' · ')
}

export function buildBestGameweekRecord(
  bestGameweek: SeasonStats['bestGameweek'],
  detail: GameweekDetail | null | undefined
): BestGameweekRecord | null {
  if (bestGameweek === null) return null
  return {
    ...bestGameweek,
    marks: marksForGameweek(detail),
    activeChips: detail?.activeChips ?? [],
  }
}

export interface ChipTilesInput {
  /** Whose chips these are. Null renders no tiles at all — see `getOtherPlayerProfile`. */
  ledger: ChipLedger | null
  seasonHistory: GameweekResult[]
  /** Gameweeks whose results are all in — the only ones a final number may be read from. */
  settledGameweeks: number[]
  currentGameweek: number
  /**
   * Whether a chip played *for the current gameweek* may be shown as live. Always true on your
   * own profile; on someone else's it is the reveal gate, because a chip played for the round in
   * play is a pre-deadline choice the app refuses to leak.
   */
  canRevealCurrentGameweek: boolean
}

export function buildChipTiles({
  ledger,
  seasonHistory,
  settledGameweeks,
  currentGameweek,
  canRevealCurrentGameweek,
}: ChipTilesInput): ChipTile[] {
  if (ledger === null) return []

  return CHIP_TILE_ORDER.map((id) => {
    const all = [...ledger[id]].sort((a, b) => a - b)
    const isLive = all.includes(currentGameweek) && canRevealCurrentGameweek
    // The live week is not yet a record, so it never joins the played refs — but a hidden
    // in-flight chip (before their deadline) isn't a played one either, so it drops out too.
    const played = all.filter((gameweek) => gameweek !== currentGameweek)

    const name = CHIP_TILE_NAMES[id]

    const seasonCap = CHIP_SEASON_CAPS[id]

    if (isLive) {
      return {
        id,
        name,
        state: 'live' as const,
        gameweek: null,
        gameweeks: played,
        points: null,
        used: Math.min(all.length, seasonCap),
        seasonCap,
      }
    }
    if (played.length === 0) {
      return {
        id,
        name,
        state: 'unplayed' as const,
        gameweek: null,
        gameweeks: [],
        points: null,
        used: 0,
        seasonCap,
      }
    }

    // A chip's number is the week's full total, so a 9-point Clean Sweep reads as the disaster it
    // was without any arithmetic. With more than one charge spent, the tile face leads with the
    // best week — the rest are in the modal, not crammed into a third of a row.
    const scored = played
      .filter((gameweek) => settledGameweeks.includes(gameweek))
      .map((gameweek) => ({
        gameweek,
        points: seasonHistory.find((row) => row.gameweek === gameweek)?.points,
      }))
      .filter((row): row is { gameweek: number; points: number } => row.points !== undefined)
    const best = scored.reduce<{ gameweek: number; points: number } | null>(
      (top, row) => (top === null || row.points > top.points ? row : top),
      null
    )

    return {
      id,
      name,
      state: 'played' as const,
      // Nothing settled yet: the face still names a week, just without a number to rank on.
      gameweek: best?.gameweek ?? played[played.length - 1],
      gameweeks: played,
      points: best?.points ?? null,
      used: Math.min(played.length, seasonCap),
      seasonCap,
    }
  })
}
